import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import {
  Wrench, Bug, AlertTriangle, CheckCircle2, Clock, Plus, Trash2,
  Tag, MessageSquare, ArrowUpCircle, ListChecks, X
} from 'lucide-react';

const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

const MaintenancePage = () => {
  const [tab, setTab] = useState('tickets');
  const [stats, setStats] = useState({});
  const [tickets, setTickets] = useState([]);
  const [versions, setVersions] = useState([]);
  const [changelogs, setChangelogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [ticketForm, setTicketForm] = useState({ title: '', description: '', priority: 'medium', category: 'bug' });
  const [versionForm, setVersionForm] = useState({ version: '', release_date: '', notes: '', status: 'stable' });
  const [changelogForm, setChangelogForm] = useState({ version: '', date: '', changesText: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [s, t, v, c] = await Promise.all([
        axios.get(`${API}/maintenance/stats`, { headers: headers() }),
        axios.get(`${API}/tickets`, { headers: headers() }),
        axios.get(`${API}/versions`),
        axios.get(`${API}/changelogs`),
      ]);
      setStats(s.data);
      setTickets(t.data);
      setVersions(v.data);
      setChangelogs(c.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createTicket = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/tickets`, ticketForm, { headers: headers() });
      toast.success('Ticket créé');
      setTicketForm({ title: '', description: '', priority: 'medium', category: 'bug' });
      setShowForm(false);
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
    setLoading(false);
  };

  const updateTicket = async (id, data) => {
    try {
      await axios.put(`${API}/tickets/${id}`, data, { headers: headers() });
      toast.success('Ticket mis à jour');
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Supprimer ?')) return;
    try {
      await axios.delete(`${API}/${type}/${id}`, { headers: headers() });
      toast.success('Supprimé');
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
  };

  const createVersion = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/versions`, versionForm, { headers: headers() });
      toast.success('Version ajoutée');
      setVersionForm({ version: '', release_date: '', notes: '', status: 'stable' });
      setShowForm(false);
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
    setLoading(false);
  };

  const createChangelog = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const changes = changelogForm.changesText.split('\n').filter(Boolean);
      await axios.post(`${API}/changelogs`, { ...changelogForm, changes }, { headers: headers() });
      toast.success('Changelog ajouté');
      setChangelogForm({ version: '', date: '', changesText: '' });
      setShowForm(false);
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
    setLoading(false);
  };

  const priorityConfig = {
    low: { color: 'bg-gray-100 text-gray-600', label: 'Basse' },
    medium: { color: 'bg-blue-100 text-blue-700', label: 'Moyenne' },
    high: { color: 'bg-orange-100 text-orange-700', label: 'Haute' },
    critical: { color: 'bg-red-100 text-red-700', label: 'Critique' },
  };

  const statusConfig = {
    open: { color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle, label: 'Ouvert' },
    in_progress: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: 'En cours' },
    resolved: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Résolu' },
    closed: { color: 'bg-gray-100 text-gray-500', icon: CheckCircle2, label: 'Fermé' },
  };

  const tabs = [
    { id: 'tickets', label: 'Tickets', icon: Bug, count: stats.open_tickets },
    { id: 'versions', label: 'Versions', icon: Tag, count: stats.total_versions },
    { id: 'changelog', label: 'Changelog', icon: ListChecks },
  ];

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-testid="maintenance-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Wrench className="text-[#35AFE4]" size={24} /> Maintenance
            </h1>
            <p className="text-sm text-gray-500 mt-1">Gestion des tickets, versions et changelog</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Tickets ouverts', val: stats.open_tickets || 0, color: '#f39c12', icon: AlertTriangle },
            { label: 'En cours', val: stats.in_progress_tickets || 0, color: '#3498db', icon: Clock },
            { label: 'Critiques', val: stats.critical_tickets || 0, color: '#e74c3c', icon: Bug },
            { label: 'Versions', val: stats.total_versions || 0, color: '#27ae60', icon: Tag },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3" data-testid={`stat-${i}`}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: s.color + '15' }}>
                <s.icon size={18} style={{ color: s.color }} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{s.val}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200 mb-6">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setShowForm(false); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? 'border-[#35AFE4] text-[#35AFE4]' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon size={16} />
              {t.label}
              {t.count != null && (
                <span className="ml-1 bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{t.count}</span>
              )}
            </button>
          ))}
          <div className="flex-1" />
          <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[#35AFE4] hover:bg-[#2d7d9e]" data-testid="add-btn">
            {showForm ? <X size={16} /> : <Plus size={16} />}
            <span className="ml-1">{showForm ? 'Fermer' : 'Nouveau'}</span>
          </Button>
        </div>

        {/* Create Forms */}
        {showForm && tab === 'tickets' && (
          <form onSubmit={createTicket} className="bg-white border rounded-xl p-6 mb-6 space-y-4" data-testid="ticket-form">
            <h3 className="font-bold text-gray-900 mb-2">Nouveau ticket</h3>
            <Input placeholder="Titre du ticket" value={ticketForm.title} onChange={e => setTicketForm(p => ({...p, title: e.target.value}))} required data-testid="ticket-title" />
            <Textarea placeholder="Description détaillée..." value={ticketForm.description} onChange={e => setTicketForm(p => ({...p, description: e.target.value}))} rows={4} required data-testid="ticket-desc" />
            <div className="flex gap-4">
              <select value={ticketForm.priority} onChange={e => setTicketForm(p => ({...p, priority: e.target.value}))} className="border rounded-lg px-3 py-2 text-sm" data-testid="ticket-priority">
                <option value="low">Basse</option>
                <option value="medium">Moyenne</option>
                <option value="high">Haute</option>
                <option value="critical">Critique</option>
              </select>
              <select value={ticketForm.category} onChange={e => setTicketForm(p => ({...p, category: e.target.value}))} className="border rounded-lg px-3 py-2 text-sm" data-testid="ticket-category">
                <option value="bug">Bug</option>
                <option value="feature">Fonctionnalité</option>
                <option value="improvement">Amélioration</option>
                <option value="question">Question</option>
              </select>
            </div>
            <Button type="submit" disabled={loading} className="bg-[#35AFE4]" data-testid="ticket-submit">
              <MessageSquare size={16} className="mr-2" /> Créer le ticket
            </Button>
          </form>
        )}

        {showForm && tab === 'versions' && (
          <form onSubmit={createVersion} className="bg-white border rounded-xl p-6 mb-6 space-y-4" data-testid="version-form">
            <h3 className="font-bold text-gray-900 mb-2">Nouvelle version</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input placeholder="Ex: 2.1.0" value={versionForm.version} onChange={e => setVersionForm(p => ({...p, version: e.target.value}))} required data-testid="version-number" />
              <Input type="date" value={versionForm.release_date} onChange={e => setVersionForm(p => ({...p, release_date: e.target.value}))} required data-testid="version-date" />
            </div>
            <Textarea placeholder="Notes de version..." value={versionForm.notes} onChange={e => setVersionForm(p => ({...p, notes: e.target.value}))} rows={3} required data-testid="version-notes" />
            <select value={versionForm.status} onChange={e => setVersionForm(p => ({...p, status: e.target.value}))} className="border rounded-lg px-3 py-2 text-sm" data-testid="version-status">
              <option value="stable">Stable</option>
              <option value="beta">Beta</option>
              <option value="alpha">Alpha</option>
            </select>
            <Button type="submit" disabled={loading} className="bg-[#35AFE4]" data-testid="version-submit">
              <ArrowUpCircle size={16} className="mr-2" /> Publiér la version
            </Button>
          </form>
        )}

        {showForm && tab === 'changelog' && (
          <form onSubmit={createChangelog} className="bg-white border rounded-xl p-6 mb-6 space-y-4" data-testid="changelog-form">
            <h3 className="font-bold text-gray-900 mb-2">Nouveau changelog</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input placeholder="Version (ex: 2.1.0)" value={changelogForm.version} onChange={e => setChangelogForm(p => ({...p, version: e.target.value}))} required data-testid="changelog-version" />
              <Input type="date" value={changelogForm.date} onChange={e => setChangelogForm(p => ({...p, date: e.target.value}))} required data-testid="changelog-date" />
            </div>
            <Textarea placeholder="Un changement par ligne..." value={changelogForm.changesText} onChange={e => setChangelogForm(p => ({...p, changesText: e.target.value}))} rows={5} required data-testid="changelog-changes" />
            <Button type="submit" disabled={loading} className="bg-[#35AFE4]" data-testid="changelog-submit">
              <ListChecks size={16} className="mr-2" /> Publiér le changelog
            </Button>
          </form>
        )}

        {/* Tickets List */}
        {tab === 'tickets' && (
          <div className="space-y-3" data-testid="tickets-list">
            {tickets.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Bug size={48} className="mx-auto mb-3 opacity-40" />
                <p className="font-medium">Aucun ticket</p>
                <p className="text-sm">Cliquez sur "Nouveau" pour créer un ticket</p>
              </div>
            ) : tickets.map(t => {
              const pr = priorityConfig[t.priority] || priorityConfig.medium;
              const st = statusConfig[t.status] || statusConfig.open;
              return (
                <div key={t.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`ticket-${t.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${st.color}`}>
                          <st.icon size={12} /> {st.label}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${pr.color}`}>{pr.label}</span>
                        <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded">{t.category}</span>
                      </div>
                      <h3 className="font-bold text-gray-900 truncate">{t.title}</h3>
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{t.description}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                        <span>Par {t.reporter_name || t.reporter_email}</span>
                        <span>{new Date(t.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <select
                        value={t.status}
                        onChange={e => updateTicket(t.id, { status: e.target.value })}
                        className="text-xs border rounded-lg px-2 py-1.5"
                      >
                        <option value="open">Ouvert</option>
                        <option value="in_progress">En cours</option>
                        <option value="resolved">Résolu</option>
                        <option value="closed">Fermé</option>
                      </select>
                      <button onClick={() => deleteItem('tickets', t.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Versions List */}
        {tab === 'versions' && (
          <div className="space-y-3" data-testid="versions-list">
            {versions.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Tag size={48} className="mx-auto mb-3 opacity-40" />
                <p className="font-medium">Aucune version</p>
              </div>
            ) : versions.map(v => (
              <div key={v.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`version-${v.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-lg font-bold text-gray-900">v{v.version}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        v.status === 'stable' ? 'bg-green-100 text-green-700' : v.status === 'beta' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                      }`}>{v.status}</span>
                    </div>
                    <p className="text-sm text-gray-500">{v.notes}</p>
                    <span className="text-xs text-gray-400 mt-2 block">Publiée le {v.release_date}</span>
                  </div>
                  <button onClick={() => deleteItem('versions', v.id)} className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Changelog */}
        {tab === 'changelog' && (
          <div className="space-y-4" data-testid="changelog-list">
            {changelogs.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <ListChecks size={48} className="mx-auto mb-3 opacity-40" />
                <p className="font-medium">Aucun changelog</p>
              </div>
            ) : changelogs.map(cl => (
              <div key={cl.id} className="bg-white border border-gray-200 rounded-xl p-5" data-testid={`changelog-${cl.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-[#35AFE4]">v{cl.version}</span>
                    <span className="text-xs text-gray-400">{cl.date}</span>
                  </div>
                  <button onClick={() => deleteItem('changelogs', cl.id)} className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50">
                    <Trash2 size={16} />
                  </button>
                </div>
                <ul className="space-y-1.5">
                  {(cl.changes || []).map((change, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                      {change}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default MaintenancePage;
